import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { vizMarkdownComponents } from '../utils/markdownComponents';


/* ─── Node progress card (one per completed node during a run) ── */

const NODE_TYPE_ICONS = {
  input: '▶',
  output: '■',
  agent: '◈',
  tool: '⚙',
  extractor: '⛏',
  merge: '⊕',
  condition: '◇',
};

const NODE_STATUS_ICONS = {
  completed: '✓',
  failed: '✗',
  skipped: '–',
  pending: '○',
  active: '●',
  running: '●',
};

const NODE_STATUS_COLORS = {
  completed: 'var(--success-text-color, #065f46)',
  failed: 'var(--error-text-color, #dc2626)',
  skipped: 'var(--text-secondary)',
  pending: 'var(--text-secondary)',
  active: 'var(--accent-color, #3b82f6)',
  running: 'var(--accent-color, #3b82f6)',
};

/**
 * Collapsible card showing one node's execution result.
 *
 * @param {string} label - Node display label.
 * @param {string} nodeType - e.g. 'agent', 'tool', 'input'.
 * @param {object} result - NodeOutput fields from the SSE stream.
 * @param {boolean} defaultExpanded - Start expanded when true.
 */
function NodeProgressCard({ label, nodeType, result, defaultExpanded }) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  const status = result?.status || 'pending';
  const content =
    typeof result?.content === 'string' ? result.content : '';
  const err = result?.error || null;
  // Live intra-node sub-agent progress (per-worker started/done, web
  // search, synthesizing). Grows while the node is running; final list
  // remains on the completed card.
  const progressSteps = Array.isArray(result?.progress)
    ? result.progress
    : [];
  const isRunning = status === 'running';

  const statusColor = NODE_STATUS_COLORS[status] || 'var(--text-secondary)';
  const statusIcon = NODE_STATUS_ICONS[status] || '○';
  const typeIcon = NODE_TYPE_ICONS[nodeType] || '◻';

  const PREVIEW_LEN = 350;
  const needsTruncation = content.length > PREVIEW_LEN && !expanded;
  const displayContent = needsTruncation
    ? content.slice(0, PREVIEW_LEN) + '…'
    : content;

  return (
    <div
      style={{
        borderLeft: `3px solid ${statusColor}`,
        paddingLeft: 10,
        marginBottom: 10,
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: err || content ? 4 : 0,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: '0.68rem',
            color: statusColor,
            flexShrink: 0,
            fontFamily: 'monospace',
          }}
        >
          {statusIcon}
        </span>
        <span
          style={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: 'var(--text-color)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '0.63rem',
            color: 'var(--text-secondary)',
            flexShrink: 0,
          }}
        >
          {typeIcon} {nodeType}
        </span>
      </div>

      {/* Error */}
      {err && (
        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--error-text-color, #dc2626)',
            background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.18)',
            borderRadius: 3,
            padding: '3px 6px',
            marginBottom: 4,
          }}
        >
          {err}
        </div>
      )}

      {/* Live sub-agent progress */}
      {progressSteps.length > 0 && (
        <div style={{ marginBottom: content ? 6 : 0 }}>
          {progressSteps.map((step, i) => {
            const failed = step.status === 'agent_failed';
            const latestRunning =
              !failed && isRunning && i === progressSteps.length - 1;
            return (
              <div
                key={`${step.ts || ''}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '1px 0',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: latestRunning
                      ? 'var(--accent-color, #3b82f6)'
                      : failed
                        ? NODE_STATUS_COLORS.failed
                        : '#28a745',
                  }}
                >
                  {latestRunning ? (
                    <div
                      className="loading-spinner"
                      style={{
                        width: '4px',
                        height: '4px',
                        border: '1.5px solid rgba(255,255,255,0.3)',
                        borderLeftColor: '#fff',
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        color: 'white',
                        fontSize: '6px',
                        lineHeight: 1,
                      }}
                    >
                      {failed ? NODE_STATUS_ICONS.failed : '✓'}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: '0.72rem',
                    color: latestRunning
                      ? 'var(--text-color)'
                      : failed
                        ? NODE_STATUS_COLORS.failed
                        : 'var(--text-secondary)',
                  }}
                >
                  {step.message || step.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Content */}
      {content && (
        <div>
          <div
            className="wf-run-markdown"
            style={{ fontSize: '0.77rem' }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={vizMarkdownComponents}
            >
              {displayContent}
            </ReactMarkdown>
          </div>
          {content.length > PREVIEW_LEN && (
            <button
              onClick={() => setExpanded((e) => !e)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--accent-color, #3b82f6)',
                fontSize: '0.7rem',
                padding: '2px 0',
                marginTop: 2,
              }}
            >
              {expanded ? 'Show less ▲' : 'Show more ▼'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


export { NODE_TYPE_ICONS, NodeProgressCard };
